import React, { useState, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SocialShare from '../../../components/ui/SocialShare';
import WishlistModal from '../../../components/ui/WishlistModal';
import { useAuth } from '../../../contexts/AuthContext';
import { saveToContact, markAsPurchased, getUserContacts } from '../../../services/userActions';

const ProductInfo = React.memo(({ product }) => {
  const [isWishlistModalOpen, setIsWishlistModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    giftIdea: false,
    purchased: false
  });
  const { isAuthenticated } = useAuth();

  // Use backend-formatted prices instead of frontend formatting
  const formattedPrice = useMemo(() => {
    return product?.formatted_price || product?.current_price || '€0.00';
  }, [product?.formatted_price, product?.current_price]);

  const formattedOriginalPrice = useMemo(() => {
    return product?.formatted_original_price || null;
  }, [product?.formatted_original_price]);

  // Calculate actual display price (discount price if available, otherwise regular price)
  const displayPrice = useMemo(() => {
    if (product?.discount_price) {
      // Use the same currency symbol as the formatted price, or default to €
      const currencyMatch = formattedPrice.match(/^[€$£¥₹₽]/);
      const currency = currencyMatch ? currencyMatch[0] : '€';
      return `${currency}${product.discount_price}`;
    }
    return formattedPrice;
  }, [product?.discount_price, formattedPrice]);

  const originalPrice = useMemo(() => {
    if (product?.discount_price && (product?.retail_price || product?.price)) {
      // Use the same currency symbol as the formatted price, or default to €
      const currencyMatch = formattedPrice.match(/^[€$£¥₹₽]/);
      const currency = currencyMatch ? currencyMatch[0] : '€';
      return `${currency}${product.retail_price || product.price}`;
    }
    return formattedOriginalPrice;
  }, [product?.discount_price, product?.retail_price, product?.price, formattedOriginalPrice, formattedPrice]);

  const renderStars = useCallback((rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={`${index < rating ? 'text-warning fill-current' : 'text-accent-300'
          }`}
      />
    ));
  }, []);

  // Memoize computed values - updated to handle fallback data more elegantly
  const rating = useMemo(() => product?.external_rating || 4.5, [product?.external_rating]);
  const reviewCount = useMemo(() => {
    const count = product?.review_count || product?.external_rating_count;
    return count || 127; // Fallback only if no real data
  }, [product?.review_count, product?.external_rating_count]);

  const handleWishlistClick = useCallback(() => {
    if (!isAuthenticated) {
      // Redirect to login or show login modal
      window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
      return;
    }
    setIsWishlistModalOpen(true);
  }, [isAuthenticated]);

  const handleBuyNowClick = useCallback(() => {
    if (product?.affiliate_url) {
      // Track affiliate link click for analytics
      try {
        fetch(`/api/v1/products/${product.id}/track-affiliate-click/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }).catch(err => console.warn('Failed to track affiliate click:', err));
      } catch (error) {
        console.warn('Failed to track affiliate click:', error);
      }

      // Open in new tab to keep user on product page
      window.open(product.affiliate_url, '_blank', 'noopener,noreferrer');
    } else {
      console.log('No affiliate URL available');
    }
  }, [product?.affiliate_url, product?.id]);

  const handleGiftIdeaClick = useCallback(() => {
    if (!isAuthenticated) {
      window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
      return;
    }
    alert('Contact selection modal coming soon');
    // TODO: Open contact selection modal
  }, [isAuthenticated]);

  const handlePurchasedClick = useCallback(() => {
    if (!isAuthenticated) {
      window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
      return;
    }
    alert('Purchase details modal coming soon');
    // TODO: Open purchase details modal
  }, [isAuthenticated]);

  const handleShareClick = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  return (
    <div className="space-y-6">
      {/* Product Title and Brand */}
      <div>
        <div className="mb-2">
          <span className="text-sm text-accent-600 font-medium">
            {product?.brand || 'Premium Brand'}
          </span>
        </div>
        <h1 className="font-heading text-3xl font-bold text-accent-800 mb-2">
          {product?.name || 'Luxury Premium Gift Set'}
        </h1>
        <p className="text-lg text-accent-600 leading-relaxed">
          {product?.description || 'A thoughtfully curated gift that shows you truly care. Perfect for special occasions and meaningful moments.'}
        </p>
      </div>

      {/* Why This Makes a Great Gift - MOVED UP */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-accent-800">
          Why This Makes a Great Gift
        </h3>
        <div className="bg-accent-50 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Gift" size={20} className="text-primary mt-0.5" />
            <div>
              <p className="text-accent-700 leading-relaxed">
                {product?.gift_features_text ||
                  product?.gift_description ||
                  "This makes a thoughtful gift that shows you care. Perfect for special occasions and meaningful moments."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rating Section - MOVED DOWN */}
      <div className="flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            {renderStars(rating)}
            <span className="ml-2 text-lg font-medium text-accent-800">
              {rating}
            </span>
            <span className="ml-1 text-sm text-accent-600">
              ({reviewCount} reviews)
            </span>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-white rounded-2xl border border-accent-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className={`text-3xl font-heading font-bold ${product?.discount_price ? 'text-red-600' : 'text-accent-800'
              }`}>
              {displayPrice}
            </span>
            {originalPrice && (
              <span className="text-lg text-accent-500 line-through ml-3">
                {originalPrice}
              </span>
            )}
          </div>
          {product?.discount_percentage && (
            <div className="bg-error text-white px-3 py-1 rounded-full text-sm font-medium">
              -{Math.round(product?.discount_percentage)}% OFF
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Primary Buy Now Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            iconName="ExternalLink"
            iconPosition="left"
            onClick={handleBuyNowClick}
            className="shadow-warm hover:shadow-primary"
          >
            Buy Now - {displayPrice}
          </Button>

          {/* Secondary Actions Row 1: Wishlist + Share */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              size="md"
              iconName="Heart"
              iconPosition="left"
              onClick={handleWishlistClick}
            >
              {isAuthenticated ? 'Add to Wishlist' : 'Login to Save'}
            </Button>

            <Button
              variant="outline"
              size="md"
              iconName="Share"
              iconPosition="left"
              onClick={handleShareClick}
            >
              Share
            </Button>
          </div>

          {/* Secondary Actions Row 2: Gift Idea + Purchased */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              size="md"
              iconName="User"
              iconPosition="left"
              onClick={handleGiftIdeaClick}
              disabled={loadingStates.giftIdea}
            >
              {loadingStates.giftIdea ? 'Loading...' : 'Add as Gift Idea'}
            </Button>

            <Button
              variant="outline"
              size="md"
              iconName="Check"
              iconPosition="left"
              onClick={handlePurchasedClick}
              disabled={loadingStates.purchased}
            >
              {loadingStates.purchased ? 'Loading...' : 'I Bought This'}
            </Button>
          </div>
        </div>
      </div>

      {/* Wishlist Modal */}
      <WishlistModal
        isOpen={isWishlistModalOpen}
        onClose={() => setIsWishlistModalOpen(false)}
        product={product}
      />

      {/* Share Modal */}
      {isShareModalOpen && (
        <SocialShare
          product={product}
          autoOpen={true}
          onClose={handleShareModalClose}
          key={`share-${Date.now()}`}
        />
      )}
    </div>
  );
});

export default ProductInfo;